import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfirmProvider } from './context/ConfirmContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import CambioPasswordTemporal from './components/auth/CambioPasswordTemporal';
import Home from './components/Home';
import Inicio from './components/Inicio';
import Landing from './components/Landing';
import Profile from './components/Profile';
import Investigadores from './components/Investigadores';
import Publicaciones from './components/Publicaciones';
import PublicacionesRefreshJobs from './components/PublicacionesRefreshJobs';
import Lineas from './components/Lineas';
import ImportCSV from './components/ImportCSV';
import AIAnalysis from './components/AIAnalysis';
import Dashboards from './components/Dashboards';
import UserManagement from './components/UserManagement';
import BusquedaPublicaciones from './components/BusquedaPublicaciones';
import FormularioIES from './components/FormularioIES';
import { NotificationContainer } from './components/Notification';
import { useNotifications } from './hooks/useNotifications';
import Header from './components/Header';
import { ROLES_CRUD } from './constants/roles';
import './styles/animations.css';

// Variantes de animación para las páginas
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.4,
};

function AppContent() {
  const [darkMode, setDarkMode] = useState(false);
  const location = useLocation();
  const notifications = useNotifications();
  const { user, isAuthenticated, isLoading } = useAuth();

  // Con sesión activa y requiere_cambio=true: solo login o pantalla de cambio (resto del sitio bloqueado)
  const passwordChangePaths = ['/login', '/cambiar-password-temporal'];
  const mustChangePassword =
    !isLoading &&
    isAuthenticated &&
    user?.requiereCambioPassword &&
    !passwordChangePaths.includes(location.pathname);

  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setDarkMode(true);
    }
  }, []);
  
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Páginas sin header (login, cambio de contraseña)
  const noHeaderPages = ['/login', '/cambiar-password-temporal'];
  const showHeader = !noHeaderPages.includes(location.pathname);

  if (mustChangePassword) {
    return <Navigate to="/cambiar-password-temporal" replace />;
  }

  return (
    <div className="bg-background-light dark:bg-background-dark text-slate-800 dark:text-slate-200 min-h-screen flex flex-col">
      {/* Sistema de Notificaciones */}
      <NotificationContainer 
        notifications={notifications.notifications}
        removeNotification={notifications.removeNotification}
      />
      
      {/* Header horizontal */}
      {showHeader && <Header darkMode={darkMode} setDarkMode={setDarkMode} />}
      
      <main className="flex-1 overflow-auto">
        <AnimatePresence mode="wait">
          <Routes>
            {/* Login */}
            <Route path="/login" element={<Login />} />
            
            {/* Cambio de contraseña temporal */}
            <Route path="/cambiar-password-temporal" element={<CambioPasswordTemporal />} />

            {/* Inicio - PÚBLICO (Página simplificada con logos) */}
            <Route path="/" element={
              <motion.div
                key="inicio"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
                className="h-full overflow-auto"
              >
                <Inicio />
              </motion.div>
            } />
            
            {/* Landing alternativo - PÚBLICO (Página estilo CEDIA completo) */}
            <Route path="/landing" element={
              <motion.div
                key="landing"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
                className="h-full overflow-auto"
              >
                <Landing />
              </motion.div>
            } />
            
            {/* Home/Sobre el Proyecto - PÚBLICO */}
            <Route path="/proyecto" element={
              <motion.div
                key="home"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
                className="h-full overflow-auto"
              >
                <Home />
              </motion.div>
            } />
            
            {/* Perfil de usuario - Requiere autenticación */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <motion.div
                  key="profile"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  className="h-full overflow-auto"
                >
                  <Profile />
                </motion.div>
              </ProtectedRoute>
            } />

            {/* Gestión de Usuarios - Solo SuperAdmin */}
            <Route path="/users" element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <motion.div
                  key="users"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  className="h-full overflow-auto"
                >
                  <UserManagement />
                </motion.div>
              </ProtectedRoute>
            } />

            <Route path="/publicaciones-jobs" element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <motion.div
                  key="publicaciones-jobs"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  className="h-full overflow-auto"
                >
                  <PublicacionesRefreshJobs />
                </motion.div>
              </ProtectedRoute>
            } />

            {/* Dashboards - PÚBLICO */}
            <Route path="/dashboards" element={
              <motion.div
                key="dashboards"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
                className="h-full overflow-auto"
              >
                <Dashboards />
              </motion.div>
            } />

            {/* Redirección: cuartiles unificados en /dashboards (pestaña SCImago) */}
            <Route path="/dashboard-cuartiles" element={<Navigate to="/dashboards" replace />} />

            {/* Investigadores - PÚBLICO */}
            <Route path="/investigadores" element={
              <motion.div
                key="investigadores"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
                className="h-full overflow-auto"
              >
                <Investigadores />
              </motion.div>
            } />

            {/* Búsqueda de Publicaciones por Investigador - Solo Admin/SuperAdmin */}
            <Route path="/busqueda-publicaciones" element={
              <ProtectedRoute allowedRoles={ROLES_CRUD}>
                <motion.div
                  key="busqueda-publicaciones"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  className="h-full overflow-auto"
                >
                  <BusquedaPublicaciones />
                </motion.div>
              </ProtectedRoute>
            } />

            <Route path="/formulario-ies" element={
              <ProtectedRoute allowedRoles={ROLES_CRUD}>
                <motion.div
                  key="formulario-ies"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  className="h-full overflow-auto"
                >
                  <FormularioIES />
                </motion.div>
              </ProtectedRoute>
            } />

            <Route path="/cuartiles" element={<Navigate to="/import" replace />} />

            {/* Publicaciones - PÚBLICO */}
            <Route path="/publicaciones" element={
              <motion.div
                key="publicaciones"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
                className="h-full overflow-auto"
              >
                <Publicaciones />
              </motion.div>
            } />

            {/* Líneas de Investigación - PÚBLICO */}
            <Route path="/lineas" element={
              <motion.div
                key="lineas"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
                className="h-full overflow-auto"
              >
                <Lineas />
              </motion.div>
            } />

            {/* Importar CSV - Requiere Admin/SuperAdmin */}
            <Route path="/import" element={
              <ProtectedRoute allowedRoles={ROLES_CRUD}>
                <motion.div
                  key="import"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  className="h-full overflow-auto"
                >
                  <ImportCSV />
                </motion.div>
              </ProtectedRoute>
            } />

            {/* Análisis IA - Requiere Admin/SuperAdmin */}
            <Route path="/ai" element={
              <ProtectedRoute allowedRoles={ROLES_CRUD}>
                <motion.div
                  key="ai"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  className="h-full overflow-auto"
                >
                  <AIAnalysis />
                </motion.div>
              </ProtectedRoute>
            } />
          </Routes>
        </AnimatePresence>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ConfirmProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ConfirmProvider>
    </Router>
  );
}

export default App;
